import { useEffect, useState, useCallback } from "react";
import { Card, CardBody, Button, CardHeader, InputGroup, Input, InputGroupText } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrash, faSpinner, faSearch, faSliders, faArchive, faUndo } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ButtonComponent from "../../../../components/ButtonComponent";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import PillNavigation from "../../../../components/SubNavBar";
import {
  useDeleteCustomMenuItemMutation,
  useLazyFetchCustomMenuListQuery,
} from "../../../../redux/services/customizeMenuService";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useUpdateAvailabilityStatusMutation } from "../../../../redux/services/menuItemService";
import { toast } from "react-toastify";
import { formatTimestamp, getImageUrlWithFallback } from "../../../../utility/Utility";
import { DEFAULT_IMAGE_URL } from "../../../../constants/constans";
import { useDispatch, useSelector } from "react-redux";
import "./index.css";
import { CustomMenuItem, setRowsPerPage } from "../../../../redux/customizeMenuSlice";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import { useRestaurantDetails } from "../../../../hooks/useFirebaseOrders";

const NAV_LIST_CUSTOMIZE_MENU = [
  {
    id: 1,
    item: "Customized Menu",
  },
  {
    id: 2,
    item: "Archived Menu",
  },
];

// Custom hook for fetching menu data
export const useFetchCustomMenus = (restaurantId: string) => {
  const [customMenus, setCustomMenus] = useState<CustomMenuItem[]>([]);
  const [trigger, { data, isLoading }] =
    useLazyFetchCustomMenuListQuery();

  useEffect(() => {
    if (restaurantId) {
      trigger(restaurantId);
    }
  }, [restaurantId, trigger]);

  useEffect(() => {
    if (data?.customMenuList) {
      setCustomMenus(data.customMenuList);
    }
  }, [data]);

  return { customMenus, isLoading, refetch: () => trigger(restaurantId) };
};

// Custom hook for handling mutations
const useMenuMutations = (restaurantId: string, refetch: () => void) => {

  const [deleteCustomMenuItem] = useDeleteCustomMenuItemMutation();
  const [updateAvailabilityStatus] = useUpdateAvailabilityStatusMutation();

  const [deletingMenuId, setDeletingMenuId] = useState<string | null>(null);
  const [archivingMenuId, setArchivingMenuId] = useState<string | null>(null);
  const [unarchivingMenuId, setUnarchivingMenuId] = useState<string | null>(null);

  const handleDelete = useCallback(
    async (item: CustomMenuItem) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#590B21",
        cancelButtonColor: "#ddd",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setDeletingMenuId(item.id);
          try {
            await deleteCustomMenuItem({
              restaurantId,
              customMenuId: item.id,
            }).unwrap();
            Swal.fire("Deleted!", "Your menu has been deleted.", "success");
            refetch();
          } catch (error) {
            Swal.fire(
              "Error!",
              "An error occurred while deleting the menu.",
              "error"
            );
          } finally {
            setDeletingMenuId(null);
          }
        }
      });
    },
    [deleteCustomMenuItem, restaurantId, refetch]
  );

  const handleToggleChange = useCallback(
    async (menuId: string, status: boolean) => {
      try {
        await updateAvailabilityStatus({
          restaurantId,
          menuId,
          menuType: "CustomMenu",
          status,
        }).unwrap();
        refetch();
      } catch (error) {
        toast.error("Error updating availability status");
      }
    },
    [updateAvailabilityStatus, restaurantId, refetch]
  );

  const handleArchiveMenu = useCallback(
    async (item: CustomMenuItem) => {
      Swal.fire({
        title: "Archive Menu?",
        text: "This menu will be archived and moved to archived tab.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#590B21",
        cancelButtonColor: "#ddd",
        confirmButtonText: "Yes, archive it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setArchivingMenuId(item.id);
          try {
            await updateAvailabilityStatus({
              restaurantId,
              menuId: item.id,
              menuType: "CustomMenu",
              status: false,
              isArchived: true,
            }).unwrap();
            Swal.fire("Archived!", "Your menu has been archived.", "success");
            refetch();
          } catch (error) {
            Swal.fire(
              "Error!",
              "An error occurred while archiving the menu.",
              "error"
            );
          } finally {
            setArchivingMenuId(null);
          }
        }
      });
    },
    [restaurantId, refetch, updateAvailabilityStatus]
  );

  const handleUnarchiveMenu = useCallback(
    async (item: CustomMenuItem) => {
      Swal.fire({
        title: "Unarchive Menu?",
        text: "This menu will be moved back to active tab.",
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#590B21",
        cancelButtonColor: "#ddd",
        confirmButtonText: "Yes, unarchive it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setUnarchivingMenuId(item.id);
          try {
            await updateAvailabilityStatus({
              restaurantId,
              menuId: item.id,
              menuType: "CustomMenu",
              status: true,
              isArchived: false,
            }).unwrap();
            Swal.fire("Restored!", "Your menu has been restored.", "success");
            refetch();
          } catch (error) {
            Swal.fire(
              "Error!",
              "An error occurred while restoring the menu.",
              "error"
            );
          } finally {
            setUnarchivingMenuId(null);
          }
        }
      });
    },
    [restaurantId, refetch, updateAvailabilityStatus]
  );

  return {
    handleDelete,
    handleToggleChange,
    deletingMenuId,
    archivingMenuId,
    unarchivingMenuId,
    handleArchiveMenu,
    handleUnarchiveMenu,
  };
};

const CustomizeMenuTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  const restaurantStatus = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.status
  );

  const rowsPerPage = useSelector((state: any) => state.customizeMenu.rowsPerPage);

  const [activeTab, setActiveTab] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMenus, setFilteredMenus] = useState<CustomMenuItem[]>([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const [filterTagsData, setFilterTagsData] = useState<any>(null);

  const { data: restaurantData, loading: isFilterTagsLoading } = useRestaurantDetails(restaurantId);
  const fetchedFilterTagsData = restaurantData?.filterTags || null;

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };

  const { customMenus, isLoading, refetch } = useFetchCustomMenus(restaurantId);
  const {
    handleDelete,
    handleToggleChange,
    deletingMenuId,
    archivingMenuId,
    unarchivingMenuId,
    handleArchiveMenu,
    handleUnarchiveMenu,
  } = useMenuMutations(
    restaurantId,
    refetch
  );
  const isArchivedTab = activeTab === 2;

  useEffect(() => {
    setFilterTagsData(fetchedFilterTagsData || null);
  }, [fetchedFilterTagsData]);

  const handleAllFilter = () => {
    setSelectedFilterTags([]);
  };

  const handleTagFilter = (tagObj: any, groupData: any) => {
    const isSelected = selectedFilterTags.includes(tagObj.tag);
    const allowMultiple =
      groupData && typeof groupData === "object" && "allowMultipleSelect" in groupData
        ? groupData.allowMultipleSelect
        : false;

    setSelectedFilterTags((prevTags) => {
      if (allowMultiple) {
        return isSelected
          ? prevTags.filter((tag) => tag !== tagObj.tag)
          : [...prevTags, tagObj.tag];
      }

      const tags = groupData && groupData.tags && Array.isArray(groupData.tags) ? groupData.tags : [];
      const otherGroupTags = tags.map((t: any) => t.tag).filter((t: string) => t !== tagObj.tag);
      const filteredTags = prevTags.filter((tag) => !otherGroupTags.includes(tag));

      if (isSelected) {
        return filteredTags.filter((tag) => tag !== tagObj.tag);
      }

      return [...filteredTags, tagObj.tag];
    });
  };

  const filterBySelectedTag = (list: CustomMenuItem[]): CustomMenuItem[] => {
    if (selectedFilterTags.length === 0 || !filterTagsData) {
      return list;
    }

    const normalizedSelectedTags = selectedFilterTags.map((tag) => tag.toLowerCase());
    const selectedGroupMap: Record<string, Set<string>> = {};
    const groupKeyMapping: Record<string, string> = {
      Brand: "brand",
      Class: "class",
      Cuisine: "cuisine",
      "New Arrivals": "new arrivals",
    };

    for (const [groupName, groupData] of Object.entries(filterTagsData)) {
      if (groupData && typeof groupData === "object" && "tags" in groupData) {
        const tags = (groupData as any).tags;
        if (Array.isArray(tags)) {
          for (const tag of normalizedSelectedTags) {
            if (tags.some((t: any) => t.tag && t.tag.toLowerCase() === tag)) {
              const itemKey = groupKeyMapping[groupName] || groupName.toLowerCase();
              if (!selectedGroupMap[itemKey]) {
                selectedGroupMap[itemKey] = new Set();
              }
              selectedGroupMap[itemKey].add(tag);
            }
          }
        }
      }
    }

    return list.filter((item) => {
      const itemTags = (item as any).tags;

      if (!itemTags || typeof itemTags !== "object") {
        return false;
      }

      for (const [itemKey, selectedTagsInGroup] of Object.entries(selectedGroupMap)) {
        const itemTagsForGroup = itemTags[itemKey] || [];

        if (Array.isArray(itemTagsForGroup)) {
          const normalizedItemTags = itemTagsForGroup.map((tag: any) =>
            typeof tag === "string" ? tag.toLowerCase() : ""
          );
          const hasMatch = Array.from(selectedTagsInGroup).some((selectedTag) =>
            normalizedItemTags.includes(selectedTag)
          );
          if (!hasMatch) {
            return false;
          }
        } else if (typeof itemTagsForGroup === "string") {
          const hasMatch = Array.from(selectedTagsInGroup).includes(itemTagsForGroup.toLowerCase());
          if (!hasMatch) {
            return false;
          }
        } else {
          return false;
        }
      }
      return true;
    });
  };

  // Filter menus based on search term
  useEffect(() => {
    if (!customMenus || customMenus.length === 0) {
      setFilteredMenus([]);
      return;
    }

    const searchFiltered =
      searchTerm.trim() === ""
        ? customMenus
        : customMenus.filter((item: CustomMenuItem) => {
            const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
            const itemText = [
              item.displayName || "",
              String((item as any).tinyId || ""),
              item.productId || "",
            ]
              .join(" ")
              .toLowerCase();
            return searchWords.every((word) => itemText.includes(word));
          });

    const archiveFiltered = searchFiltered.filter((item: CustomMenuItem) =>
      isArchivedTab ? (item as any).isArchived === true : !(item as any).isArchived
    );

    setFilteredMenus(filterBySelectedTag(archiveFiltered));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customMenus, searchTerm, selectedFilterTags, filterTagsData, isArchivedTab]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleAddNewCustomizeMenu = () => {
    if (restaurantStatus === "Pending") {
      toast.warning("Your restaurant is still pending approval.");
    } else {
      navigate("/productCatalogue/products/add-product");
    }
  };

  const columns: TableColumn<CustomMenuItem>[] = [
    {
      name: <h6 className="fw-bold">Image</h6>,
      cell: (row) => (
        <img
          src={getImageUrlWithFallback(row.imageUrl, DEFAULT_IMAGE_URL, 64) || DEFAULT_IMAGE_URL}
          alt=""
          className="customize-menu-item-image"
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      cell: (row) => <span title={row.displayName}>{row.displayName}</span>,
    },
    {
      name: <h6 className="fw-bold">SKU ID</h6>,
      selector: (row) => row.productId || "",
    },
    {
      name: <h6 className="fw-bold">Tiny ID</h6>,
      selector: (row) => row.tinyId || "",
    },
    // {
    //   name: <h6 className="fw-bold">Total Items</h6>,
    //   selector: (row) => row.totalItems,
    // },
    {
      name: <h6 className="fw-bold">Price(Rs.)</h6>,
      selector: (row) => row.price,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row) => formatTimestamp(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Updated Date</h6>,
      selector: (row:any) => formatTimestamp(row.updatedAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Active</h6>,
      cell: (row: CustomMenuItem) => (
        <ToggleSwitch
          initialChecked={row.isAvailable}
          menuId={row.id}
          onChange={handleToggleChange}
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="light"
            onClick={() =>
              navigate(`/productCatalogue/products/update-product`, {
                state: { menu: row },
              })
            }
            className="custom-white-disabled-button"
          >
            <FontAwesomeIcon icon={faEye} title="Edit" color="#FF9A62"
            />
          </Button>
          {isArchivedTab ? (
            unarchivingMenuId === row.id ? (
              <Button variant="light" disabled>
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="customize-menu-fontawesome-icon"
                />
              </Button>
            ) : (
              <Button variant="light" onClick={() => handleUnarchiveMenu(row)} className="custom-white-disabled-button">
                <FontAwesomeIcon
                  icon={faUndo}
                  title="Unarchive"
                  color="#2E7D32"
                  className="customize-menu-fontawesome-icon"
                />
              </Button>
            )
          ) : (
            archivingMenuId === row.id ? (
              <Button variant="light" disabled>
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="customize-menu-fontawesome-icon"
                />
              </Button>
            ) : (
              <Button variant="light" onClick={() => handleArchiveMenu(row)} className="custom-white-disabled-button">
                <FontAwesomeIcon
                  icon={faArchive}
                  title="Archive"
                  color="#5C6BC0"
                  className="customize-menu-fontawesome-icon"
                />
              </Button>
            )
          )}

          {deletingMenuId === row.id ? (
            <Button variant="light" disabled>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="customize-menu-fontawesome-icon"
              />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDelete(row)} className="custom-white-disabled-button">
              <FontAwesomeIcon
                icon={faTrash}
                title="Delete"
                color="#F24E1E"
                className="customize-menu-fontawesome-icon"
              />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <BreadCrumbs hasSubMenus={true} />
      <div className="customize-menu-container">
        {!isArchivedTab && (
          <div className="d-flex justify-content-end w-100">
            <ButtonComponent titel="Add" onClick={handleAddNewCustomizeMenu} />
          </div>
        )}
        <Card className="customize-menu-card">
          <CardHeader className="customize-menu-card-header">
            <h4>{isArchivedTab ? "Archived Customized Menu" : "Customized Menu"}</h4>
          </CardHeader>
          <CardBody>
            <PillNavigation
              navList={NAV_LIST_CUSTOMIZE_MENU}
              active={activeTab}
              toggle={setActiveTab}
            />
            {isLoading ? (
              <TableLoadingComponent numColumns={5}/>
            ) : (
              <>
                {/* Search Input */}
                <div className="pb-3">
                  <InputGroup>
                    <InputGroupText>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder={isArchivedTab ? "Search archived menus by name or SKU ID..." : "Search customize menus by name or SKU ID..."}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </InputGroup>

                  {isFilterTagsLoading ? (
                    <div className="filter-tags-container mt-3">
                      <div className="d-flex align-items-center mb-3">
                        <FontAwesomeIcon icon={faSliders} className="me-2" />
                        <h6 className="mb-0 fw-bold">Filter Items</h6>
                      </div>

                      <div className="filter-tags-scroll-container">
                        <div className="filter-tags-wrapper">
                          {[...Array(8)].map((_, index) => (
                            <div
                              key={`skeleton-${index}`}
                              className="filter-tag-skeleton"
                              style={{
                                width: `${60 + Math.random() * 40}px`,
                                height: "32px",
                                borderRadius: "16px",
                                margin: "0 8px 8px 0",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    filterTagsData &&
                    Object.keys(filterTagsData).length > 0 && (
                      <div className="filter-tags-container mt-3">
                        <div className="d-flex align-items-center mb-3">
                          <FontAwesomeIcon icon={faSliders} className="me-2" />
                          <h6 className="mb-0 fw-bold">Filter Items</h6>
                        </div>

                        <div className="filter-tags-scroll-container">
                          <div className="filter-tags-wrapper">
                            <button
                              className={`filter-tag-btn ${selectedFilterTags.length === 0 ? "active" : ""}`}
                              onClick={handleAllFilter}
                            >
                              All
                            </button>

                            {Object.entries(filterTagsData).map(
                              ([category, categoryData]: [string, any], categoryIndex: number) => {
                                const visibleTags = categoryData.tags?.filter((tag: any) => tag.show) || [];

                                if (visibleTags.length === 0) return null;

                                return (
                                  <div key={category} className="d-flex align-items-center">
                                    {categoryIndex > 0 && <div className="filter-category-separator" />}
                                    {visibleTags.map((tagObj: any, index: number) => {
                                      const isSelected = selectedFilterTags.includes(tagObj.tag);

                                      return (
                                        <button
                                          key={`${category}-${tagObj.tag}-${index}`}
                                          className={`filter-tag-btn ${isSelected ? "active" : ""}`}
                                          onClick={() => handleTagFilter(tagObj, categoryData)}
                                        >
                                          {tagObj.tag}
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>

                        {selectedFilterTags.length > 0 && (
                          <div className="mt-2">
                            <small className="text-muted">
                              {selectedFilterTags.length} filter
                              {selectedFilterTags.length !== 1 ? "s" : ""} applied
                            </small>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
                <DataTable
                  columns={columns}
                  data={filteredMenus}
                  pagination
                  highlightOnHover
                  paginationPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  noDataComponent={
                    <div className="py-4 text-center">
                      {searchTerm
                        ? `No menus found for "${searchTerm}"`
                        : selectedFilterTags.length > 0
                          ? "No menus match selected filters"
                          : isArchivedTab
                            ? "No archived menus available"
                            : "No menus available"}
                    </div>
                  }
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default CustomizeMenuTable;
